import React, { useMemo } from 'react';
import { Icon, Typography, makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  getISODay,
  isSameDay,
  isSameMonth,
  isToday,
  isValid,
} from 'date-fns';
import { useTranslation } from 'react-i18next';
import { SCHEDULE_TAB_TYPE, getScheduleColour } from '../../../constants';

const MONTH_DAY_ICON_LIMIT = 2;
const WEEK_STARTS_ON = 1;

const useStyles = makeStyles(() => ({
  monthView: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    height: '100%',
    overflow: 'hidden',
    border: '1px solid #DADCE0',
    borderRadius: 8,
    backgroundColor: '#FFFFFF',
  },
  weekdayRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
    flexShrink: 0,
    borderBottom: '1px solid #DADCE0',
    backgroundColor: '#FFFFFF',
  },
  weekdayCell: {
    padding: '8px 4px 4px',
    textAlign: 'center',
    fontSize: '0.95rem',
    fontWeight: 500,
    color: '#70757a',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    lineHeight: 1.2,
  },
  weekdayToday: {
    color: '#1A73E8',
  },
  weeksGrid: {
    flex: 1,
    minHeight: 0,
    display: 'grid',
    gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
  },
  dayCell: {
    position: 'relative',
    minWidth: 0,
    minHeight: 0,
    display: 'grid',
    gridTemplateRows: 'auto auto',
    alignContent: 'start',
    justifyItems: 'center',
    rowGap: 2,
    borderRight: '1px solid #DADCE0',
    borderBottom: '1px solid #DADCE0',
    backgroundColor: '#FFFFFF',
    padding: '1px 2px 3px',
    overflow: 'hidden',
    '&:nth-child(7n)': {
      borderRight: 'none',
    },
  },
  dayCellOutside: {
    backgroundColor: '#FFFFFF',
  },
  dayCellToday: {
    backgroundColor: '#E8F0FE',
  },
  dayCellClickable: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#F8F9FA',
    },
  },
  dayCellTodayClickable: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#D2E3FC',
    },
  },
  dayDateHeader: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    margin: 0,
    padding: 0,
    lineHeight: 1,
  },
  dayDateNumber: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1rem',
    fontWeight: 400,
    color: '#3c4043',
    lineHeight: 1,
  },
  dayDateNumberToday: {
    color: '#1A73E8',
    fontWeight: 700,
  },
  dayDateNumberOutside: {
    color: '#70757a',
  },
  dayEventsRow: {
    boxSizing: 'border-box',
    width: '100%',
    maxWidth: '100%',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '5px',
    marginTop: 0,
    overflow: 'hidden',
  },
  taskIconCircle: {
    boxSizing: 'border-box',
    flex: '0 0 30px',
    width: '30px !important',
    height: '30px !important',
    minWidth: '30px !important',
    maxWidth: '30px !important',
    minHeight: '30px !important',
    maxHeight: '30px !important',
    padding: '0 !important',
    margin: 0,
    border: '2px solid #DDE9EF',
    borderRadius: '50%',
    backgroundColor: '#FFFFFF',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    overflow: 'hidden',
    lineHeight: 0,
    '&:hover': {
      boxShadow: '0px 2px 6px rgba(11, 159, 217, 0.14)',
    },
  },
  taskIconCircleDone: {
    opacity: 0.72,
  },
  taskIcon: {
    fontSize: '1.35rem !important',
    width: '1.35rem !important',
    height: '1.35rem !important',
    lineHeight: '1 !important',
  },
  moreButton: {
    boxSizing: 'border-box',
    flex: '0 0 30px',
    width: '30px !important',
    height: '30px !important',
    minWidth: '30px !important',
    maxWidth: '30px !important',
    padding: '0 !important',
    margin: 0,
    border: '1px solid #DDE9EF',
    borderRadius: '50%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFFFFF',
    cursor: 'pointer',
    color: '#3c4043',
    fontSize: '0.85rem',
    fontWeight: 700,
    lineHeight: 1,
    whiteSpace: 'nowrap',
    '&:hover': {
      backgroundColor: '#F3FAFD',
      boxShadow: '0px 2px 6px rgba(11, 159, 217, 0.14)',
      color: '#0B9FD9',
    },
  },
}));

const MonthView = (props) => {
  const { data, focusMonth, onItemClick, onOpenDayDialog } = props;
  const classes = useStyles();
  const { t } = useTranslation();

  const monthAnchor = useMemo(() => {
    const parsed = focusMonth ? new Date(focusMonth) : new Date();
    return isValid(parsed) ? parsed : new Date();
  }, [focusMonth]);

  const weekdayLabels = useMemo(() => {
    const start = startOfWeek(monthAnchor, { weekStartsOn: WEEK_STARTS_ON });
    const end = endOfWeek(monthAnchor, { weekStartsOn: WEEK_STARTS_ON });
    return eachDayOfInterval({ start, end });
  }, [monthAnchor]);

  const monthDays = useMemo(() => {
    const gridStart = startOfWeek(startOfMonth(monthAnchor), { weekStartsOn: WEEK_STARTS_ON });
    const gridEnd = endOfWeek(endOfMonth(monthAnchor), { weekStartsOn: WEEK_STARTS_ON });

    return eachDayOfInterval({ start: gridStart, end: gridEnd }).map((day) => {
      const tasks = (data || []).filter((item) => {
        if (!item?.scheduleDate) return false;
        const itemDate = new Date(item.scheduleDate);
        return isValid(itemDate) && isSameDay(itemDate, day);
      });
      return {
        date: day,
        tasks,
        inMonth: isSameMonth(day, monthAnchor),
      };
    });
  }, [data, monthAnchor]);

  const isFocusingCurrentMonth = isSameMonth(monthAnchor, new Date());
  const todayIsoDay = getISODay(new Date());
  const weekCount = Math.max(monthDays.length / 7, 1);

  const handleOpenItem = (event, task) => {
    event.stopPropagation();
    if (onItemClick) onItemClick(task);
  };

  const handleOpenDay = (event, date) => {
    event.stopPropagation();
    if (onOpenDayDialog) onOpenDayDialog(date);
  };

  const renderDayEvents = (date, tasks) => {
    if (!tasks.length) return null;

    const hasOverflow = tasks.length > MONTH_DAY_ICON_LIMIT;
    const visibleLimit = hasOverflow ? MONTH_DAY_ICON_LIMIT - 1 : MONTH_DAY_ICON_LIMIT;
    const visibleItems = tasks.slice(0, visibleLimit);
    const hiddenCount = Math.max(tasks.length - visibleLimit, 0);

    return (
      <div className={clsx(classes.dayEventsRow)}>
        {visibleItems.map((task) => {
          const typeInfo = SCHEDULE_TAB_TYPE[task.type] || {};
          const itemColour = getScheduleColour(task);
          return (
            <button
              key={task?.id}
              type="button"
              className={clsx(classes.taskIconCircle, task?.isDone && classes.taskIconCircleDone)}
              style={{ borderColor: itemColour }}
              title={task?.title || typeInfo.label || ''}
              onClick={(event) => handleOpenItem(event, task)}
            >
              <Icon className={clsx(classes.taskIcon)} style={{ color: itemColour }}>
                {typeInfo.icon || 'event'}
              </Icon>
            </button>
          );
        })}
        {hiddenCount > 0 && (
          <button
            type="button"
            className={clsx(classes.moreButton)}
            aria-label={t('dashboard:SCHEDULE_OPEN_DAY')}
            title={t('dashboard:SCHEDULE_DAY_COUNT', { count: tasks.length })}
            onClick={(event) => handleOpenDay(event, date)}
          >
            {`${hiddenCount}+`}
          </button>
        )}
      </div>
    );
  };

  return (
    <div className={clsx(classes.monthView)}>
      <div className={clsx(classes.weekdayRow)}>
        {weekdayLabels.map((day, index) => (
          <Typography
            key={day.toISOString()}
            className={clsx(
              classes.weekdayCell,
              isFocusingCurrentMonth && todayIsoDay === index + 1 && classes.weekdayToday,
              'cursor-default',
            )}
          >
            {format(day, 'EEE')}
          </Typography>
        ))}
      </div>
      <div className={clsx(classes.weeksGrid)} style={{ gridTemplateRows: `repeat(${weekCount}, minmax(0, 1fr))` }}>
        {monthDays.map(({ date, tasks, inMonth }) => {
          const today = isToday(date);
          const taskCount = tasks.length;
          const hasTasks = taskCount > 0;
          const isSingleTaskDay = taskCount === 1;

          const handleDateClick = () => {
            if (!hasTasks) return;
            if (isSingleTaskDay) {
              if (onItemClick) onItemClick(tasks[0]);
              return;
            }
            if (onOpenDayDialog) onOpenDayDialog(date);
          };

          return (
            // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
            <div
              key={date.toISOString()}
              className={clsx(
                classes.dayCell,
                !inMonth && classes.dayCellOutside,
                today && classes.dayCellToday,
                hasTasks && (today ? classes.dayCellTodayClickable : classes.dayCellClickable),
              )}
              onClick={hasTasks ? handleDateClick : undefined}
            >
              <div className={clsx(classes.dayDateHeader)}>
                <span
                  className={clsx(
                    classes.dayDateNumber,
                    today && classes.dayDateNumberToday,
                    !inMonth && !today && classes.dayDateNumberOutside,
                  )}
                >
                  {format(date, 'd')}
                </span>
              </div>
              {renderDayEvents(date, tasks)}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MonthView;
